/** Waveform scope — real-time oscilloscope visualization via AnalyserNode. */

import { useCallback, useEffect, useRef } from "react";

/** Props for the WaveformScope component. */
export type WaveformScopeProps = {
	readonly analyser: AnalyserNode | null;
};

export function WaveformScope({ analyser }: WaveformScopeProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animationRef = useRef<number>(0);

	const draw = useCallback(() => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext("2d");
		if (!canvas || !ctx || !analyser) {
			animationRef.current = requestAnimationFrame(draw);
			return;
		}

		const bufferLength = analyser.frequencyBinCount;
		const dataArray = new Uint8Array(bufferLength);
		analyser.getByteTimeDomainData(dataArray);

		const width = canvas.width;
		const height = canvas.height;
		const dpr = window.devicePixelRatio || 1;

		// Scale canvas for retina
		if (canvas.width !== canvas.clientWidth * dpr) {
			canvas.width = canvas.clientWidth * dpr;
			canvas.height = canvas.clientHeight * dpr;
		}

		ctx.fillStyle = "#0a0014";
		ctx.fillRect(0, 0, width, height);

		// Draw center line
		ctx.strokeStyle = "rgba(255, 255, 255, 0.06)";
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(0, height / 2);
		ctx.lineTo(width, height / 2);
		ctx.stroke();

		// Draw waveform
		ctx.strokeStyle = "#a855f7";
		ctx.lineWidth = 2 * dpr;
		ctx.beginPath();

		const sliceWidth = width / bufferLength;
		let x = 0;

		for (let i = 0; i < bufferLength; i++) {
			const v = (dataArray[i] ?? 128) / 128.0;
			const y = (v * height) / 2;

			if (i === 0) {
				ctx.moveTo(x, y);
			} else {
				ctx.lineTo(x, y);
			}

			x += sliceWidth;
		}

		ctx.lineTo(width, height / 2);
		ctx.stroke();

		animationRef.current = requestAnimationFrame(draw);
	}, [analyser]);

	useEffect(() => {
		animationRef.current = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(animationRef.current);
	}, [draw]);

	return (
		<div className="scope-container">
			<canvas ref={canvasRef} className="waveform-scope" />
		</div>
	);
}
